import React, { useEffect, useState } from "react";
import { RoleContext, Role } from "./RoleContext";
import { listUsers, type AppUser, addLog } from "./adminApi";

type Props = React.PropsWithChildren<{}>;

const FALLBACK_ADMIN_EMAIL = "info@rs-solutions.be";
const FALLBACK_PASSWORD = "CPAS2025!";

const SESSION_KEY = "cpas_alert_unlocked";
const ROLE_KEY = "cpas_alert_role";
const LAST_EMAIL_KEY = "cpas_alert_last_email";

const USER_FIRST_KEY = "cpas_user_first";
const USER_LAST_KEY  = "cpas_user_last";
const USER_EMAIL_KEY = "cpas_user_email";
const USER_ID_KEY    = "cpas_user_id";

const LockWrapper: React.FC<Props> = ({ children }) => {
  const [email, setEmail] = useState<string>(() => localStorage.getItem(LAST_EMAIL_KEY) || "");
  const [password, setPassword] = useState<string>("");
  const [showPwd, setShowPwd] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [unlocked, setUnlocked] = useState<boolean>(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [role, setRole] = useState<Role>(() => (sessionStorage.getItem(ROLE_KEY) as Role) || "user");

  useEffect(() => {
    if (!unlocked) setTimeout(() => (document.getElementById("login-email") as HTMLInputElement | null)?.focus(), 50);
  }, [unlocked]);

  const persistUserIdentity = (u: { firstName?: string; lastName?: string; email: string; id?: string }) => {
    sessionStorage.setItem(USER_EMAIL_KEY, u.email);
    if (u.firstName) sessionStorage.setItem(USER_FIRST_KEY, u.firstName); else sessionStorage.removeItem(USER_FIRST_KEY);
    if (u.lastName)  sessionStorage.setItem(USER_LAST_KEY,  u.lastName);  else sessionStorage.removeItem(USER_LAST_KEY);
    if (u.id)        sessionStorage.setItem(USER_ID_KEY,    u.id);       else sessionStorage.removeItem(USER_ID_KEY);
  };

  const logSafely = async (...args: Parameters<typeof addLog>) => { try { await addLog(...args); } catch {} };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    const mail = (email || "").trim().toLowerCase();

    try {
      const users: AppUser[] = await listUsers();
      const match = users.find((u) => u.active !== false && u.email === mail && u.password === password);

      if (match) {
        sessionStorage.setItem(SESSION_KEY, "1");
        sessionStorage.setItem(ROLE_KEY, match.role);
        localStorage.setItem(LAST_EMAIL_KEY, mail);
        setRole(match.role);
        persistUserIdentity({ firstName: match.firstName, lastName: match.lastName, email: match.email, id: match.id });
        setUnlocked(true);
        await logSafely({ type: "login_success", email: match.email, firstName: match.firstName, lastName: match.lastName, userRole: match.role, details: "Login via users" });
        setLoading(false);
        return;
      }

      if (mail === FALLBACK_ADMIN_EMAIL.toLowerCase() && password === FALLBACK_PASSWORD) {
        sessionStorage.setItem(SESSION_KEY, "1");
        sessionStorage.setItem(ROLE_KEY, "admin");
        localStorage.setItem(LAST_EMAIL_KEY, mail);
        setRole("admin");
        persistUserIdentity({ firstName: "Admin", lastName: "CPAS", email: mail });
        setUnlocked(true);
        await logSafely({ type: "login_success", email: mail, firstName: "Admin", lastName: "CPAS", userRole: "admin", details: "Login fallback" });
        setLoading(false);
        return;
      }

      setError("Identifiant ou mot de passe incorrect.");
      await logSafely({ type: "login_failure", email: mail, details: "Bad credentials" });
    } catch {
      if (mail === FALLBACK_ADMIN_EMAIL.toLowerCase() && password === FALLBACK_PASSWORD) {
        sessionStorage.setItem(SESSION_KEY, "1");
        sessionStorage.setItem(ROLE_KEY, "admin");
        localStorage.setItem(LAST_EMAIL_KEY, mail);
        setRole("admin");
        persistUserIdentity({ firstName: "Admin", lastName: "CPAS", email: mail });
        setUnlocked(true);
        await logSafely({ type: "login_success", email: mail, firstName: "Admin", lastName: "CPAS", userRole: "admin", details: "Login fallback (DB indisponible)" });
      } else {
        setError("Impossible de vérifier les accès (réseau/permissions).");
        await logSafely({ type: "login_failure", email: mail, details: "DB indisponible" });
      }
    } finally { setLoading(false); }
  };

  const handleLogout = async () => {
    const mail  = sessionStorage.getItem(USER_EMAIL_KEY) || undefined;
    const first = sessionStorage.getItem(USER_FIRST_KEY) || undefined;
    const last  = sessionStorage.getItem(USER_LAST_KEY)  || undefined;
    const r     = (sessionStorage.getItem(ROLE_KEY) as Role) || role;
    await logSafely({ type: "logout", email: mail, firstName: first, lastName: last, userRole: r });

    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(USER_FIRST_KEY);
    sessionStorage.removeItem(USER_LAST_KEY);
    sessionStorage.removeItem(USER_EMAIL_KEY);
    sessionStorage.removeItem(USER_ID_KEY);
    setPassword("");
    setUnlocked(false);
    setTimeout(() => (document.getElementById("login-email") as HTMLInputElement | null)?.focus(), 50);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") void handleSubmit(); };

  if (unlocked) {
    return (
      <RoleContext.Provider value={role}>
        <div style={{ minHeight: "100vh", position: "relative" }}>
          <button
            onClick={handleLogout}
            style={{ position: "absolute", top: 16, right: 16, zIndex: 1200, background: "white", color: "#111827",
                     border: "1px solid #e5e7eb", borderRadius: 12, padding: "8px 12px", fontSize: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", cursor: "pointer" }}>
            Verrouiller
          </button>
          <div style={{ minHeight: "100vh" }}>{children}</div>
        </div>
      </RoleContext.Provider>
    );
  }

  // ——— Page de connexion (symétrique) ———
  const fieldBase: React.CSSProperties = {
    width: "100%", height: 44, borderRadius: 10, border: "1px solid #cfd6e4", fontSize: 15, boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #f4f6f8 0%, #e9eef5 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "min(460px, 95vw)", background: "#fff", borderRadius: 16, boxShadow: "0 12px 32px rgba(0,0,0,0.08)", padding: 24 }}>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <img src="/logo.png" alt="CPAS" style={{ width: 64, height: 64, objectFit: "contain", opacity: 0.9 }}
               onError={(e) => ((e.target as HTMLImageElement).style.display = "none")} />
          <h1 style={{ margin: "12px 0 4px", color: "#234993", fontSize: 22 }}>Accès CPAS Alert</h1>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
            Veuillez saisir votre <strong>identifiant</strong> (e-mail) et le <strong>mot de passe provisoire</strong>.
          </p>
        </div>

        <label htmlFor="login-email" style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 6 }}>
          Identifiant (adresse e-mail)
        </label>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input id="login-email" type="email" placeholder="agent@cpasframeries.be"
                 value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={handleKeyDown}
                 autoCapitalize="none" autoCorrect="off" spellCheck={false}
                 style={{ ...fieldBase, padding: "0 12px" }} required />
        </div>

        <label htmlFor="login-password" style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 6 }}>
          Mot de passe provisoire
        </label>
        <div style={{ position: "relative", marginBottom: 12 }}>
          <input id="login-password" type={showPwd ? "text" : "password"} value={password}
                 onChange={(e) => setPassword(e.target.value)} onKeyDown={handleKeyDown}
                 style={{ ...fieldBase, padding: "0 92px 0 12px" }} required />
          <span role="button" tabIndex={0}
                onClick={() => setShowPwd((s) => !s)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowPwd((s) => !s); } }}
                aria-label={showPwd ? "Masquer" : "Afficher"}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                         cursor: "pointer", userSelect: "none", fontSize: 13, padding: "6px 8px", color: "#234993" }}>
            {showPwd ? "Masquer" : "Afficher"}
          </span>
        </div>

        {error && (
          <div role="alert" style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca",
                                     borderRadius: 8, padding: "8px 10px", fontSize: 13, marginBottom: 8 }}>
            {error}
          </div>
        )}

        <button type="button" onClick={() => void handleSubmit()} disabled={loading}
                style={{ width: "100%", height: 44, background: "#234993", color: "white", border: "none",
                         borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1 }}>
          {loading ? "Vérification..." : "Entrer"}
        </button>

        <div style={{ marginTop: 12, fontSize: 12, color: "#6b7280", textAlign: "center" }}>
          Utilisez les identifiants fournis par l’administrateur.
        </div>
      </div>
    </div>
  );
};

export default LockWrapper;
